import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Drawer,
    Grid,
    IconButton,
    InputAdornment,
    LinearProgress,
    ListItemText,
    Menu,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import {
    Close as CloseIcon,
    CloudSync as CloudSyncIcon,
    FilterAlt as FilterAltIcon,
    History as HistoryIcon,
    ManageSearch as ManageSearchIcon,
    Refresh as RefreshIcon,
    RestartAlt as ResetIcon,
    Search as SearchIcon,
    ViewColumn as ViewColumnIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import config from '../config/config';

/**
 * DV-314: Amway requires an auditable record of each IBO's name, IBO number, home address, email,
 * mobile phone, dates of participation and consent. One row per IBO (their latest version); clicking
 * opens every version they have, newest first, with what changed and who changed it.
 */

const BASE = '/um/v1/admin/user-audit';

const ACTIONS = [
    'BASELINE', 'USER_CREATED', 'PROFILE_UPDATED', 'VERIFICATION_CHANGED', 'UPLINE_CHANGED', 'TEAM_CHANGED',
    'ADDRESS_ADDED', 'ADDRESS_UPDATED', 'ADDRESS_REMOVED', 'ADDRESS_DEFAULT_CHANGED',
    'AFFILIATION_STARTED', 'AFFILIATION_ENDED', 'AMWAY_STATUS_CHANGED', 'CONSENT_GRANTED', 'CONSENT_REVOKED',
    'PASSWORD_CHANGED',
];

const ACTION_COLOR = {
    AFFILIATION_STARTED: 'success',
    CONSENT_GRANTED: 'success',
    AFFILIATION_ENDED: 'error',
    CONSENT_REVOKED: 'error',
    AMWAY_STATUS_CHANGED: 'info',
    VERIFICATION_CHANGED: 'info',
    ADDRESS_ADDED: 'secondary',
    ADDRESS_UPDATED: 'secondary',
    ADDRESS_REMOVED: 'secondary',
    ADDRESS_DEFAULT_CHANGED: 'secondary',
    UPLINE_CHANGED: 'warning',
    TEAM_CHANGED: 'warning',
    PROFILE_UPDATED: 'primary',
};

const ACTOR_LABEL = { USER: 'Member', ADMIN: 'Admin', SYSTEM: 'System', AMWAY: 'Amway' };

const FIELD_LABEL = {
    ibo_number: 'IBO number',
    ibo_name: 'Name',
    user_created_at: 'Signed up',
    invited_by_ibo_number: 'Invited by',
    email: 'Email',
    email_verified: 'Email verified',
    phone: 'Mobile phone',
    phone_verified: 'Phone verified',
    secondary_phone: 'Secondary phone',
    secondary_phone_verified: 'Secondary phone verified',
    user_verified: 'Account approved',
    is_temp_password: 'Temporary password',
    upline_platinum_ibo: 'Upline platinum IBO',
    upline_override: 'Upline set by admin',
    team_name: 'Team',
    pin_level: 'Pin level',
    is_first_contract_year: 'First contract year',
    is_registered: 'Registered with EVI',
    affiliation_started_at: 'Participation start',
    affiliation_ended_at: 'Participation end',
    amway_status: 'Amway status',
    payment_eligible: 'Payment eligible',
    amway_application_date: 'Amway application date',
    consent_provided: 'Consent to share with Amway',
    address: 'Address',
};

const EMPTY_FILTERS = {
    registered: '',
    consent: '',
    hasAddress: '',
    action: '',
    actorType: '',
    changedFrom: '',
    changedTo: '',
};

const formatDateTime = (value) => (value ? new Date(value).toLocaleString() : '—');
const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : '—');

const formatValue = (value) => {
    if (value === null || value === undefined || value === '') return '—';
    if (value === true) return 'Yes';
    if (value === false) return 'No';
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)) return formatDateTime(value);
    return String(value);
};

const homeAddress = (entry) => {
    if (!entry?.addressLine1) return null;
    return [entry.addressLine1, entry.addressLine2, entry.addressCity,
        [entry.addressState, entry.addressZip].filter(Boolean).join(' '),
        entry.addressCountry && entry.addressCountry !== 'US' ? entry.addressCountry : null]
        .filter(Boolean).join(', ');
};

const consentChip = (value) => {
    if (value === true) return <Chip size="small" color="success" label="Yes" />;
    if (value === false) return <Chip size="small" color="error" label="No" />;
    return <Chip size="small" variant="outlined" label="Unknown" />;
};

const participation = (entry) => {
    if (entry.registered) {
        return entry.affiliationStartedAt ? `Since ${formatDate(entry.affiliationStartedAt)}` : 'Registered (start unknown)';
    }
    if (entry.affiliationEndedAt) {
        return `${formatDate(entry.affiliationStartedAt)} → ${formatDate(entry.affiliationEndedAt)}`;
    }
    return 'Not registered';
};

// Local midnight of the picked day; the "to" day is inclusive, so it ends at the next midnight
const toInstant = (day, endOfDay) => {
    if (!day) return null;
    const d = new Date(`${day}T00:00:00`);
    if (endOfDay) d.setDate(d.getDate() + 1);
    return d.toISOString();
};

const SummaryCard = ({ label, value, hint, color }) => (
    <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, border: 1, borderColor: 'divider', height: '100%' }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="h5" sx={{ fontWeight: 700, color }}>{value ?? '—'}</Typography>
        {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
    </Paper>
);

const SnapshotRow = ({ label, value }) => (
    <Stack direction="row" spacing={1} sx={{ py: 0.25 }}>
        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 170, flexShrink: 0 }}>{label}</Typography>
        <Typography variant="caption" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>{formatValue(value)}</Typography>
    </Stack>
);

// Every field a version holds, grouped. The first group is the list Amway's terms require.
const SNAPSHOT_GROUPS = [
    {
        title: 'Required by Amway',
        fields: [
            ['Full name', (e) => e.iboName],
            ['IBO number', (e) => e.iboNumber],
            ['Home address', (e) => homeAddress(e)],
            ['Email', (e) => e.email],
            ['Mobile phone', (e) => e.phone],
            ['Registered with EVI', (e) => e.registered],
            ['Participation start', (e) => e.affiliationStartedAt],
            ['Participation end', (e) => e.affiliationEndedAt],
            ['Consent to share', (e) => e.consentProvided],
            ['Consent reported', (e) => e.consentObservedAt],
        ],
    },
    {
        title: 'Account',
        fields: [
            ['Signed up', (e) => e.userCreatedAt],
            ['Invited by IBO', (e) => e.invitedByIboNumber],
            ['Account approved', (e) => e.userVerified],
            ['Temporary password', (e) => e.tempPassword],
            ['First contract year', (e) => e.firstContractYear],
        ],
    },
    {
        title: 'Contact verification',
        fields: [
            ['Email verified', (e) => e.emailVerified],
            ['Phone verified', (e) => e.phoneVerified],
            ['Secondary phone', (e) => e.secondaryPhone],
            ['Secondary phone verified', (e) => e.secondaryPhoneVerified],
        ],
    },
    {
        title: 'Hierarchy',
        fields: [
            ['Upline platinum IBO', (e) => e.uplinePlatinumIbo],
            ['Upline set by admin', (e) => e.uplineOverride],
            ['Team', (e) => e.teamName],
            ['Pin level', (e) => e.pinLevel],
        ],
    },
    {
        title: 'Amway',
        fields: [
            ['Amway status', (e) => e.amwayStatus],
            ['Payment eligible', (e) => e.paymentEligible],
            ['Amway application date', (e) => e.amwayApplicationDate],
        ],
    },
];

const describeAddress = (a) => [
    a.label ? `${a.label}:` : null,
    [a.line1, a.line2, a.city, [a.state, a.zip].filter(Boolean).join(' '),
        a.country && a.country !== 'US' ? a.country : null].filter(Boolean).join(', '),
    a.isDefault ? '(home)' : null,
].filter(Boolean).join(' ');

const FullSnapshot = ({ entry }) => (
    <Stack spacing={1}>
        {SNAPSHOT_GROUPS.map((group) => (
            <Box key={group.title}>
                <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.6 }}>{group.title}</Typography>
                {group.fields.map(([label, read]) => <SnapshotRow key={label} label={label} value={read(entry)} />)}
            </Box>
        ))}
        <Box>
            <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                All addresses ({Array.isArray(entry.addresses) ? entry.addresses.length : 0})
            </Typography>
            {Array.isArray(entry.addresses) && entry.addresses.length > 0
                ? entry.addresses.map((a) => (
                    <Typography key={a.id} variant="caption" sx={{ display: 'block', py: 0.25 }}>{describeAddress(a)}</Typography>
                ))
                : <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>None on file</Typography>}
        </Box>
    </Stack>
);

// Optional list columns, picked from the Columns menu
const EXTRA_COLUMNS = [
    { key: 'teamName', label: 'Team', value: (e) => e.teamName },
    { key: 'pinLevel', label: 'Pin level', value: (e) => e.pinLevel },
    { key: 'uplinePlatinumIbo', label: 'Upline', value: (e) => e.uplinePlatinumIbo },
    { key: 'uplineOverride', label: 'Upline set by admin', value: (e) => e.uplineOverride },
    { key: 'amwayStatus', label: 'Amway status', value: (e) => e.amwayStatus },
    { key: 'paymentEligible', label: 'Payment eligible', value: (e) => e.paymentEligible },
    { key: 'amwayApplicationDate', label: 'Amway application', value: (e) => e.amwayApplicationDate },
    { key: 'firstContractYear', label: 'First contract year', value: (e) => e.firstContractYear },
    { key: 'userVerified', label: 'Account approved', value: (e) => e.userVerified },
    { key: 'emailVerified', label: 'Email verified', value: (e) => e.emailVerified },
    { key: 'phoneVerified', label: 'Phone verified', value: (e) => e.phoneVerified },
    { key: 'secondaryPhone', label: 'Secondary phone', value: (e) => e.secondaryPhone },
    { key: 'invitedByIboNumber', label: 'Invited by', value: (e) => e.invitedByIboNumber },
    { key: 'userCreatedAt', label: 'Signed up', value: (e) => e.userCreatedAt },
];

const COLUMNS_KEY = 'userAudit.extraColumns';

const loadColumns = () => {
    try {
        const saved = JSON.parse(localStorage.getItem(COLUMNS_KEY) || '[]');
        return Array.isArray(saved) ? saved.filter((k) => EXTRA_COLUMNS.some((c) => c.key === k)) : [];
    } catch {
        return [];
    }
};

const UserAuditPage = () => {
    const theme = useTheme();
    const { getAccessToken } = useAuth();
    const API_BASE_URL = config.API_BASE_URL;

    const [entries, setEntries] = useState([]);
    const [total, setTotal] = useState(0);
    const [summary, setSummary] = useState(null);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(20);
    const [sort, setSort] = useState({ by: 'lastChangedAt', dir: 'desc' });
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [selected, setSelected] = useState(null);
    const [trail, setTrail] = useState(null);
    const [loadingTrail, setLoadingTrail] = useState(false);

    const [sync, setSync] = useState(null);
    const [extraColumns, setExtraColumns] = useState(loadColumns);
    const [columnsAnchor, setColumnsAnchor] = useState(null);
    const [expandedVersions, setExpandedVersions] = useState({});
    const [confirmSync, setConfirmSync] = useState(false);
    const requestRef = useRef(0);

    const callApi = useCallback(async (path, { method = 'GET' } = {}) => {
        const token = await getAccessToken();
        if (!token) throw new Error('Authentication token not available');
        const res = await fetch(`${API_BASE_URL}${path}`, {
            method,
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.message || `${res.status}`);
        return data.data !== undefined ? data.data : data;
    }, [API_BASE_URL, getAccessToken]);

    useEffect(() => {
        const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(0); }, 350);
        return () => clearTimeout(t);
    }, [searchInput]);

    const filterParams = useCallback(() => {
        const params = new URLSearchParams();
        if (search) params.set('search', search);
        ['registered', 'consent', 'hasAddress', 'action', 'actorType'].forEach((key) => {
            if (filters[key] !== '') params.set(key, filters[key]);
        });
        const from = toInstant(filters.changedFrom, false);
        const to = toInstant(filters.changedTo, true);
        if (from) params.set('changedFrom', from);
        if (to) params.set('changedTo', to);
        return params;
    }, [search, filters]);

    const load = useCallback(async () => {
        const request = ++requestRef.current;
        setLoading(true);
        setError(null);
        try {
            const params = filterParams();
            const listParams = new URLSearchParams(params);
            listParams.set('page', page);
            listParams.set('size', pageSize);
            listParams.set('sortBy', sort.by);
            listParams.set('sortDir', sort.dir);
            const [list, counts] = await Promise.all([
                callApi(`${BASE}/list?${listParams}`),
                callApi(`${BASE}/list/summary?${params}`),
            ]);
            if (request !== requestRef.current) return;
            setEntries(list?.entries || []);
            setTotal(list?.pagination?.totalElements || 0);
            setSummary(counts);
        } catch (err) {
            if (request === requestRef.current) setError(`Failed to load the user audit: ${err.message}`);
        } finally {
            if (request === requestRef.current) setLoading(false);
        }
    }, [callApi, filterParams, page, pageSize, sort]);

    useEffect(() => { load(); }, [load]);

    // Sync status: read once, then poll while a run is going
    const loadSync = useCallback(async () => {
        try {
            setSync(await callApi(`${BASE}/amway-sync`));
        } catch {
            // the page works without it
        }
    }, [callApi]);

    useEffect(() => { loadSync(); }, [loadSync]);

    useEffect(() => {
        if (!sync?.running) return undefined;
        const t = setInterval(async () => {
            const status = await callApi(`${BASE}/amway-sync`).catch(() => null);
            if (!status) return;
            setSync(status);
            if (!status.running) load();
        }, 3000);
        return () => clearInterval(t);
    }, [sync?.running, callApi, load]);

    const startSync = async () => {
        setConfirmSync(false);
        try {
            setSync(await callApi(`${BASE}/amway-sync`, { method: 'POST' }));
        } catch (err) {
            setError(`Could not start the Amway sync: ${err.message}`);
        }
    };

    const openTrail = async (entry) => {
        setSelected(entry);
        setTrail(null);
        setLoadingTrail(true);
        try {
            setTrail(await callApi(`${BASE}/${entry.userId}/trail`));
        } catch (err) {
            setError(`Failed to load the history: ${err.message}`);
        } finally {
            setLoadingTrail(false);
        }
    };

    const setFilter = (key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
        setPage(0);
    };

    const clearFilters = () => {
        setFilters(EMPTY_FILTERS);
        setSearchInput('');
        setPage(0);
    };

    const sortBy = (by) => {
        setSort((prev) => ({ by, dir: prev.by === by && prev.dir === 'desc' ? 'asc' : 'desc' }));
        setPage(0);
    };

    const toggleColumn = (key) => {
        setExtraColumns((prev) => {
            const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
            try { localStorage.setItem(COLUMNS_KEY, JSON.stringify(next)); } catch { /* per-viewer nicety only */ }
            return next;
        });
    };

    const shownColumns = EXTRA_COLUMNS.filter((c) => extraColumns.includes(c.key));

    const toggleVersion = (id) => setExpandedVersions((prev) => ({ ...prev, [id]: !prev[id] }));

    const sortable = (by, label) => (
        <TableSortLabel active={sort.by === by} direction={sort.by === by ? sort.dir : 'desc'} onClick={() => sortBy(by)}>
            {label}
        </TableSortLabel>
    );

    const hasFilters = search || Object.values(filters).some((v) => v !== '');
    const latest = trail?.[0];

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            <Paper
                elevation={0}
                sx={{
                    p: 2.5,
                    mb: 2.5,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #00695c 0%, #004d40 100%)',
                    color: 'common.white',
                }}
            >
                <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={1.5}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Avatar sx={{ bgcolor: 'rgba(255,255,255,.18)' }}><ManageSearchIcon /></Avatar>
                        <Box>
                            <Typography variant="h5" sx={{ fontWeight: 700 }}>User Audit</Typography>
                            <Typography variant="body2" sx={{ opacity: 0.85 }}>
                                Every change to an IBO's profile, address, participation and consent
                            </Typography>
                        </Box>
                    </Stack>
                    <Stack direction="row" spacing={1.5}>
                        <Tooltip title="Ask Amway for every IBO's status, payment eligibility and consent">
                            <span>
                                <Button
                                    variant="contained"
                                    startIcon={sync?.running ? <CircularProgress size={16} color="inherit" /> : <CloudSyncIcon />}
                                    disabled={!!sync?.running}
                                    onClick={() => setConfirmSync(true)}
                                    sx={{ bgcolor: 'rgba(255,255,255,.2)', '&:hover': { bgcolor: 'rgba(255,255,255,.3)' } }}
                                >
                                    {sync?.running ? 'Syncing…' : 'Sync from Amway'}
                                </Button>
                            </span>
                        </Tooltip>
                        <Button
                            variant="contained"
                            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                            disabled={loading}
                            onClick={load}
                            sx={{ bgcolor: 'rgba(255,255,255,.2)', '&:hover': { bgcolor: 'rgba(255,255,255,.3)' } }}
                        >
                            Refresh
                        </Button>
                    </Stack>
                </Stack>
            </Paper>

            {sync?.startedAt && (
                <Alert severity={sync.running ? 'info' : 'success'} sx={{ mb: 2 }}>
                    {sync.running
                        ? `Amway sync in progress: ${sync.processed} of ${sync.total} IBOs checked, ${sync.changed} changed.`
                        : `Last Amway sync (${sync.startedBy}) finished ${formatDateTime(sync.finishedAt)}: `
                          + `${sync.processed} checked, ${sync.changed} changed, ${sync.notFound} not found at Amway, ${sync.failed} failed.`}
                    {sync.running && sync.total > 0 && (
                        <LinearProgress variant="determinate" value={(100 * sync.processed) / sync.total} sx={{ mt: 1 }} />
                    )}
                </Alert>
            )}

            {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}

            <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
                <Grid size={{ xs: 6, md: 2 }}><SummaryCard label="IBOs" value={summary?.users} /></Grid>
                <Grid size={{ xs: 6, md: 2 }}><SummaryCard label="Registered with EVI" value={summary?.registered} /></Grid>
                <Grid size={{ xs: 6, md: 2 }}>
                    <SummaryCard label="Consent: yes" value={summary?.consentYes} color={theme.palette.success.main} />
                </Grid>
                <Grid size={{ xs: 6, md: 2 }}>
                    <SummaryCard
                        label="Consent: no / unknown"
                        value={summary ? `${summary.consentNo} / ${summary.consentUnknown}` : null}
                        color={theme.palette.error.main}
                    />
                </Grid>
                <Grid size={{ xs: 6, md: 2 }}><SummaryCard label="With home address" value={summary?.withHomeAddress} /></Grid>
                <Grid size={{ xs: 6, md: 2 }}>
                    <SummaryCard
                        label="Registered, start unknown"
                        value={summary?.registeredWithoutStartDate}
                        hint="Registered before the event log began"
                    />
                </Grid>
            </Grid>

            <Paper elevation={0} sx={{ p: 2, mb: 2.5, borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                    <FilterAltIcon fontSize="small" color="action" />
                    <Typography variant="subtitle2">Filters</Typography>
                    {hasFilters && (
                        <Button size="small" startIcon={<ResetIcon />} onClick={clearFilters}>Clear</Button>
                    )}
                </Stack>
                <Grid container spacing={1.5}>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="IBO number, name, email or phone"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                        />
                    </Grid>
                    <Grid size={{ xs: 6, md: 2 }}>
                        <TextField select fullWidth size="small" label="Registered" value={filters.registered}
                            onChange={(e) => setFilter('registered', e.target.value)}>
                            <MenuItem value="">Any</MenuItem>
                            <MenuItem value="true">Yes</MenuItem>
                            <MenuItem value="false">No</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid size={{ xs: 6, md: 2 }}>
                        <TextField select fullWidth size="small" label="Consent" value={filters.consent}
                            onChange={(e) => setFilter('consent', e.target.value)}>
                            <MenuItem value="">Any</MenuItem>
                            <MenuItem value="YES">Yes</MenuItem>
                            <MenuItem value="NO">No</MenuItem>
                            <MenuItem value="UNKNOWN">Unknown</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid size={{ xs: 6, md: 2 }}>
                        <TextField select fullWidth size="small" label="Home address" value={filters.hasAddress}
                            onChange={(e) => setFilter('hasAddress', e.target.value)}>
                            <MenuItem value="">Any</MenuItem>
                            <MenuItem value="true">On file</MenuItem>
                            <MenuItem value="false">Missing</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid size={{ xs: 6, md: 2 }}>
                        <TextField select fullWidth size="small" label="Changed by" value={filters.actorType}
                            onChange={(e) => setFilter('actorType', e.target.value)}>
                            <MenuItem value="">Anyone</MenuItem>
                            {Object.entries(ACTOR_LABEL).map(([value, label]) => (
                                <MenuItem key={value} value={value}>{label}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <TextField select fullWidth size="small" label="Has a change of type" value={filters.action}
                            onChange={(e) => setFilter('action', e.target.value)}>
                            <MenuItem value="">Any</MenuItem>
                            {ACTIONS.map((a) => <MenuItem key={a} value={a}>{a.replace(/_/g, ' ').toLowerCase()}</MenuItem>)}
                        </TextField>
                    </Grid>
                    <Grid size={{ xs: 6, md: 2 }}>
                        <TextField fullWidth size="small" type="date" label="Changed from" InputLabelProps={{ shrink: true }}
                            value={filters.changedFrom} onChange={(e) => setFilter('changedFrom', e.target.value)} />
                    </Grid>
                    <Grid size={{ xs: 6, md: 2 }}>
                        <TextField fullWidth size="small" type="date" label="Changed to" InputLabelProps={{ shrink: true }}
                            value={filters.changedTo} onChange={(e) => setFilter('changedTo', e.target.value)} />
                    </Grid>
                </Grid>
            </Paper>

            {/* inline-size containment: on a narrow window the table scrolls inside this card instead of
                widening the whole layout, whose main column grows to fit its widest child */}
            <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden', contain: 'inline-size' }}>
                <Stack direction="row" justifyContent="flex-end" sx={{ px: 1.5, pt: 1 }}>
                    <Button size="small" startIcon={<ViewColumnIcon />} onClick={(e) => setColumnsAnchor(e.currentTarget)}>
                        Columns{shownColumns.length ? ` (+${shownColumns.length})` : ''}
                    </Button>
                    <Menu anchorEl={columnsAnchor} open={!!columnsAnchor} onClose={() => setColumnsAnchor(null)}>
                        {EXTRA_COLUMNS.map((c) => (
                            <MenuItem key={c.key} dense onClick={() => toggleColumn(c.key)}>
                                <Checkbox size="small" checked={extraColumns.includes(c.key)} sx={{ p: 0.5, mr: 1 }} />
                                <ListItemText primary={c.label} />
                            </MenuItem>
                        ))}
                    </Menu>
                </Stack>
                {loading && <LinearProgress />}
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{sortable('iboNumber', 'IBO')}</TableCell>
                                <TableCell>{sortable('iboName', 'Name, email, mobile')}</TableCell>
                                <TableCell>Home address</TableCell>
                                <TableCell>Participation</TableCell>
                                <TableCell>Consent</TableCell>
                                {shownColumns.map((c) => <TableCell key={c.key} sx={{ whiteSpace: 'nowrap' }}>{c.label}</TableCell>)}
                                <TableCell align="right">{sortable('versionCount', 'Changes')}</TableCell>
                                <TableCell>{sortable('lastChangedAt', 'Last change')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {entries.map((entry) => (
                                <TableRow key={entry.userId} hover sx={{ cursor: 'pointer' }} onClick={() => openTrail(entry)}>
                                    <TableCell sx={{ fontFamily: 'monospace' }}>{entry.iboNumber}</TableCell>
                                    <TableCell sx={{ maxWidth: 280 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{entry.iboName || '—'}</Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-all' }}>
                                            {entry.email || '—'}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">{entry.phone || '—'}</Typography>
                                    </TableCell>
                                    <TableCell sx={{ maxWidth: 220 }}>
                                        {homeAddress(entry) || <Typography variant="caption" color="error">Missing</Typography>}
                                    </TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{participation(entry)}</TableCell>
                                    <TableCell>{consentChip(entry.consentProvided)}</TableCell>
                                    {shownColumns.map((c) => (
                                        <TableCell key={c.key} sx={{ whiteSpace: 'nowrap' }}>{formatValue(c.value(entry))}</TableCell>
                                    ))}
                                    <TableCell align="right">{entry.versionCount}</TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                        <Stack spacing={0.25}>
                                            <Chip size="small" label={entry.action} color={ACTION_COLOR[entry.action] || 'default'}
                                                sx={{ alignSelf: 'flex-start' }} />
                                            <Typography variant="caption" color="text.secondary">
                                                {formatDateTime(entry.changedAt)} · {ACTOR_LABEL[entry.actorType] || entry.actorType}
                                            </Typography>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {!loading && entries.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7 + shownColumns.length} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                        No IBOs match these filters
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={total}
                    page={page}
                    rowsPerPage={pageSize}
                    rowsPerPageOptions={[20, 50, 100]}
                    onPageChange={(e, p) => setPage(p)}
                    onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
                />
            </Paper>

            <Drawer
                anchor="right"
                open={!!selected}
                onClose={() => setSelected(null)}
                PaperProps={{ sx: { width: { xs: '100%', md: 600 } } }}
            >
                {selected && (
                    <Box sx={{ p: 2.5 }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                            <Stack direction="row" alignItems="center" spacing={1.2}>
                                <Avatar sx={{ bgcolor: '#00695c' }}><HistoryIcon /></Avatar>
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 700 }}>{selected.iboName || selected.email}</Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                        IBO {selected.iboNumber} · user {selected.userId}
                                    </Typography>
                                </Box>
                            </Stack>
                            <IconButton onClick={() => setSelected(null)}><CloseIcon /></IconButton>
                        </Stack>

                        {loadingTrail && <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress /></Stack>}

                        {latest && (
                            <Paper elevation={0} sx={{ p: 1.5, mb: 2, borderRadius: 2, border: 1, borderColor: 'divider' }}>
                                <Typography variant="subtitle2">What we hold today</Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                    Version {latest.version}, {formatDateTime(latest.changedAt)}
                                </Typography>
                                <FullSnapshot entry={latest} />
                            </Paper>
                        )}

                        <Stack spacing={1.5}>
                            {(trail || []).map((version) => (
                                <Paper
                                    key={version.id}
                                    elevation={0}
                                    sx={{
                                        p: 1.5,
                                        borderRadius: 2,
                                        borderLeft: '4px solid',
                                        borderLeftColor: `${ACTION_COLOR[version.action] || 'grey'}.main`,
                                        bgcolor: 'action.hover',
                                    }}
                                >
                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5, flexWrap: 'wrap' }}>
                                        <Chip size="small" label={`v${version.version}`} variant="outlined" />
                                        <Chip size="small" label={version.action} color={ACTION_COLOR[version.action] || 'default'} />
                                        <Typography variant="caption" color="text.secondary">{formatDateTime(version.changedAt)}</Typography>
                                    </Stack>
                                    <Typography variant="caption" sx={{ display: 'block' }}>
                                        {ACTOR_LABEL[version.actorType] || version.actorType}: {version.changedBy}
                                    </Typography>
                                    {version.source && (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontFamily: 'monospace' }}>
                                            {version.source}
                                        </Typography>
                                    )}
                                    {Array.isArray(version.changes) && version.changes.length > 0 ? (
                                        <Table size="small" sx={{ mt: 1 }}>
                                            <TableBody>
                                                {version.changes.map((c, i) => (
                                                    <TableRow key={i}>
                                                        <TableCell sx={{ pl: 0, width: 150, color: 'text.secondary', fontSize: 12 }}>
                                                            {FIELD_LABEL[c.field] || c.field}
                                                        </TableCell>
                                                        <TableCell sx={{ fontSize: 12, textDecoration: 'line-through', color: 'text.secondary' }}>
                                                            {formatValue(c.old)}
                                                        </TableCell>
                                                        <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>{formatValue(c.new)}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    ) : (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                            {version.action === 'PASSWORD_CHANGED'
                                                ? 'Password changed (never stored here)'
                                                : 'First snapshot of this IBO'}
                                        </Typography>
                                    )}
                                    <Button size="small" sx={{ mt: 0.5, px: 0 }} onClick={() => toggleVersion(version.id)}>
                                        {expandedVersions[version.id] ? 'Hide full snapshot' : `Full snapshot at v${version.version}`}
                                    </Button>
                                    {expandedVersions[version.id] && (
                                        <Box sx={{ mt: 1, p: 1.5, borderRadius: 1.5, bgcolor: 'background.paper' }}>
                                            <FullSnapshot entry={version} />
                                        </Box>
                                    )}
                                </Paper>
                            ))}
                            {trail && trail.length === 0 && (
                                <Typography variant="body2" color="text.disabled" sx={{ textAlign: 'center', py: 3 }}>
                                    No history yet
                                </Typography>
                            )}
                        </Stack>
                    </Box>
                )}
            </Drawer>

            <Dialog open={confirmSync} onClose={() => setConfirmSync(false)}>
                <DialogTitle>Sync from Amway?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This asks Amway's validate API about every IBO, one at a time, and records any change in
                        their status, payment eligibility or consent. With a few thousand IBOs it takes several
                        minutes and runs in the background.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmSync(false)}>Cancel</Button>
                    <Button variant="contained" onClick={startSync}>Start sync</Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default UserAuditPage;
